/**
 * createLocalStorageAdapter — Phase 28 H4.
 *
 * Adapts a browser-storage object (defaulting to `window.localStorage`)
 * to the `{get, set}` StorageAdapter shape consumed by
 * `createMarketingAnalytics`. The adapter is the seam that absorbs the
 * browser REFUSING storage (a `SecurityError` from a blocked origin, a
 * `QuotaExceededError` from a full or zero quota — see
 * `internal/webStorageRefusal.js`), so callers never defend against it:
 * a refused read is "nothing stored", a refused write keeps the value
 * out of storage. With no storage at all (SSR, `storage: undefined`) it
 * is a no-op adapter. Anything ELSE a storage call throws is a defect and
 * is rethrown (SWALLOWED-CATCH-BURNDOWN-001 U3: every catch here used to
 * absorb everything, so a defect vanished exactly like a refusal).
 *
 * @module @puffin-core/marketing-shell/analytics/createLocalStorageAdapter
 */

import { isStorageRefusal } from '../internal/webStorageRefusal.js';

/**
 * @typedef {Object} BrowserStorage
 * @property {(key: string) => string|null} getItem
 * @property {(key: string, value: string) => void} setItem
 */

/**
 * @typedef {Object} LocalStorageAdapterConfig
 * @property {BrowserStorage} [storage]   Defaults to `globalThis.localStorage`.
 */

/**
 * Build the adapter.
 *
 * @param {LocalStorageAdapterConfig} [config]
 * @returns {{get: (key: string) => string|null, set: (key: string, value: string) => void}}
 */
export default function createLocalStorageAdapter(config = {}) {
  // Distinguish between "no `storage` key" (use global) and "`storage:
  // undefined`" (explicit no-op for tests / SSR).
  const storage = 'storage' in config ? config.storage : safeGlobalLocalStorage();

  return {
    get(key) {
      if (!storage || typeof storage.getItem !== 'function') return null;
      try {
        const v = storage.getItem(key);
        return v === undefined ? null : v;
      } catch (error) {
        if (!isStorageRefusal(error)) throw error;
        return null;
      }
    },
    set(key, value) {
      if (!storage || typeof storage.setItem !== 'function') return;
      try {
        storage.setItem(key, String(value));
      } catch (error) {
        // A refused write leaves this value in memory only: the analytics session id is minted
        // again on the next event, and attribution is re-read from the next URL. Nothing the
        // visitor sees depends on it.
        if (!isStorageRefusal(error)) throw error;
      }
    },
  };
}

function safeGlobalLocalStorage() {
  try {
    return globalThis.localStorage;
  } catch (error) {
    // The getter throws `SecurityError` when the browser blocks storage for the page: there is
    // no storage, and the adapter is a no-op.
    if (!isStorageRefusal(error)) throw error;
    return undefined;
  }
}
